import React, { useState } from "react";
import { Table, Button, Tag, Space, Typography, message, Modal, Popconfirm, Statistic, Card } from "antd";
import { useQuery } from "@tanstack/react-query";
import { getUsers, deleteUser } from "./api";
import { EditOutlined, DeleteOutlined, UserOutlined, TeamOutlined, KeyOutlined } from "@ant-design/icons";
import ChangePasswordModal from "./ChangePasswordModal";

const { Title } = Typography;

interface UsersTableProps {
  onEditUser: (userId: number) => void;
}

interface User {
  id: number;
  phone: string;
  roleId: number;
  role: {
    id: number;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

const UsersTable: React.FC<UsersTableProps> = ({ onEditUser }) => {
  const [changePasswordModal, setChangePasswordModal] = useState<{
    visible: boolean;
    userId: number;
    userName: string;
  }>({
    visible: false,
    userId: 0,
    userName: '',
  });

  const { data: users, isLoading, error, refetch } = useQuery({ 
    queryKey: ["users"], 
    queryFn: getUsers 
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'admin': return 'red';
      case 'instructor': return 'orange';
      case 'student': return 'blue';
      default: return 'default';
    }
  };

  const getRoleName = (roleName: string) => {
    switch (roleName) {
      case 'admin': return 'Администратор';
      case 'instructor': return 'Преподаватель';
      case 'student': return 'Студент';
      default: return roleName;
    }
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      await deleteUser(userId);
      message.success('Пользователь успешно удален');
      refetch();
    } catch (error: any) {
      message.error(error.message || 'Ошибка удаления пользователя');
    }
  };

  const handleChangePassword = (userId: number, userName: string) => {
    setChangePasswordModal({
      visible: true,
      userId,
      userName,
    });
  };

  const handlePasswordChangeSuccess = () => {
    setChangePasswordModal({ visible: false, userId: 0, userName: '' });
    refetch();
  };

  const columns = [
    { 
      title: "ID", 
      dataIndex: "id", 
      key: "id", 
      width: 80 
    },
    { 
      title: "Телефон", 
      dataIndex: "phone", 
      key: "phone",
      width: 150,
      render: (phone: string) => (
        <div style={{ fontWeight: '500' }}>
          {phone}
        </div>
      )
    },
    {
      title: "Роль",
      dataIndex: ["role", "name"],
      key: "role",
      width: 150,
      render: (roleName: string) => (
        <Tag color={getRoleColor(roleName)} style={{ fontWeight: '500' }}>
          {getRoleName(roleName)}
        </Tag>
      ),
    },
    { 
      title: "Зарегистрирован", 
      dataIndex: "createdAt", 
      key: "createdAt",
      width: 150,
      render: (date: string) => formatDate(date)
    },
    { 
      title: "Обновлён", 
      dataIndex: "updatedAt", 
      key: "updatedAt",
      width: 150,
      render: (date: string) => formatDate(date)
    },
    {
      title: "Действия",
      key: "actions",
      width: 280,
      render: (_: any, record: User) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => onEditUser(record.id)}
            size="small"
          >
            Редактировать
          </Button>
          <Button
            type="default"
            icon={<KeyOutlined />}
            onClick={() => handleChangePassword(record.id, record.phone)}
            size="small"
          >
            Пароль
          </Button>
          <Popconfirm
            title="Удалить пользователя"
            description="Вы уверены, что хотите удалить этого пользователя? Это действие нельзя отменить."
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Да, удалить"
            cancelText="Отмена"
            okType="danger"
          >
            <Button
              type="default"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) return <div>Загрузка...</div>;
  if (error) return <div>Ошибка: {(error as Error).message}</div>;

  // Статистика
  const adminUsers = users?.filter((u: any) => u.role.name === 'admin').length || 0;
  const instructorUsers = users?.filter((u: any) => u.role.name === 'instructor').length || 0;
  const studentUsers = users?.filter((u: any) => u.role.name === 'student').length || 0;

  return (
    <div>
      {/* Статистика пользователей */}
      <div style={{ 
        marginBottom: '24px', 
        padding: '16px', 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px' 
        }}>
          <Statistic 
            title="Всего пользователей" 
            value={users?.length || 0} 
            prefix={<TeamOutlined />}
            valueStyle={{ color: '#1976d2' }}
          />
          <Statistic 
            title="Администраторы" 
            value={adminUsers} 
            prefix={<Tag color="red">Админ</Tag>}
            valueStyle={{ color: '#d32f2f' }}
          />
          <Statistic 
            title="Преподаватели" 
            value={instructorUsers} 
            prefix={<Tag color="orange">Преподаватель</Tag>}
            valueStyle={{ color: '#f57c00' }}
          />
          <Statistic 
            title="Студенты" 
            value={studentUsers} 
            prefix={<Tag color="blue">Студент</Tag>}
            valueStyle={{ color: '#1976d2' }}
          />
        </div>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ margin: 0, color: "#1976d2" }}>
            Пользователи системы
          </Title>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <ChangePasswordModal
        visible={changePasswordModal.visible}
        userId={changePasswordModal.userId}
        userName={changePasswordModal.userName}
        onCancel={() => setChangePasswordModal({ visible: false, userId: 0, userName: '' })}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
};

export default UsersTable; 